import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";
import api, { setSession, errMsg } from "@/lib/api";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

export default function VerifyOtp() {
    const nav = useNavigate();
    const email = sessionStorage.getItem("ddi_signup_email") || "";
    const initialOtp = sessionStorage.getItem("ddi_dev_otp") || "";
    const [otp, setOtp] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (initialOtp) {
            toast("Dev OTP", {
                description: `Your OTP is ${initialOtp} — auto-fill enabled for demo.`,
                duration: 7000,
            });
        }
    }, [initialOtp]);

    const submit = async (e) => {
        e?.preventDefault();
        setBusy(true);
        try {
            const { data } = await api.post("/auth/verify-otp", {
                email,
                otp: otp || initialOtp,
            });
            setSession(data.access_token, data.user);
            sessionStorage.removeItem("ddi_signup_email");
            sessionStorage.removeItem("ddi_dev_otp");
            toast.success("Email verified — welcome!");
            nav("/doctor");
        } catch (err) {
            toast.error(errMsg(err, "OTP verification failed"));
        } finally {
            setBusy(false);
        }
    };

    const resend = async () => {
        try {
            const { data } = await api.post("/auth/resend-otp", { email });
            if (data?.dev_otp) {
                sessionStorage.setItem("ddi_dev_otp", data.dev_otp);
                toast("New Dev OTP", {
                    description: `Your new OTP is ${data.dev_otp}.`,
                    duration: 8000,
                });
            } else {
                toast.info("OTP re-sent (if the email is registered).");
            }
        } catch (err) {
            toast.error(errMsg(err, "Could not resend OTP"));
        }
    };

    return (
        <div className="min-h-screen bg-[#F5F7FB] flex items-center justify-center p-6 relative">
            <div className="absolute inset-0 ddi-dot-grid-dark opacity-40 pointer-events-none" aria-hidden />
            <div className="ddi-card ddi-shadow-md w-full max-w-md p-8 sm:p-9 relative" data-testid="verify-otp-panel">
                <Link to="/" className="flex items-center gap-2.5 w-fit" data-testid="verify-otp-brand">
                    <img src="/brand-icon.png" alt="Doctor Digital ID" className="h-10 w-10 object-contain" />
                    <span className="text-[15px] font-bold tracking-tight text-[#123B5D]">
                        DOCTOR <span className="text-[#2563EB]">DIGITAL ID</span>
                    </span>
                </Link>
                <div className="mt-6 inline-flex items-center justify-center h-11 w-11 rounded-xl bg-[#0F766E]/10 text-[#0F766E]">
                    <KeyRound size={18} />
                </div>
                <h1 className="mt-4 text-[26px] font-bold text-[#123B5D] tracking-tight">
                    Verify your account
                </h1>
                <p className="mt-1.5 text-[13.5px] text-[#64748B] leading-relaxed">
                    OTP sent to your <span className="font-semibold text-[#123B5D]">WhatsApp and email</span>
                    {email ? (
                        <>
                            {" "}
                            (<span className="font-semibold text-[#123B5D]">{email}</span>)
                        </>
                    ) : null}
                    . Enter the 6-digit code below.
                </p>
                <div className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-[#FEF3C7] text-[#B45309] text-[11px] font-semibold px-2 py-1">
                    DEV MODE — code shown in toast above
                </div>
                <form onSubmit={submit} className="mt-6">
                    <Label>OTP</Label>
                    <div className="mt-2">
                        <InputOTP
                            maxLength={6}
                            value={otp || initialOtp}
                            onChange={(v) => setOtp(v)}
                            containerClassName="justify-start"
                            data-testid="verify-otp-input"
                        >
                            <InputOTPGroup>
                                {[0, 1, 2, 3, 4, 5].map((i) => (
                                    <InputOTPSlot key={i} index={i} />
                                ))}
                            </InputOTPGroup>
                        </InputOTP>
                    </div>
                    <button
                        type="submit"
                        className="mt-6 w-full ddi-teal-btn disabled:opacity-60"
                        disabled={busy}
                        data-testid="verify-otp-submit"
                    >
                        {busy ? "Verifying…" : "Verify & continue"}
                    </button>
                </form>
                <div className="mt-4 flex items-center justify-between text-sm">
                    <button
                        onClick={resend}
                        className="text-[#0F766E] font-semibold hover:underline"
                        data-testid="verify-otp-resend"
                    >
                        Resend OTP
                    </button>
                    <Link to="/login" className="text-[#17212B]/60 hover:text-[#123B5D]" data-testid="verify-otp-login">
                        Back to login
                    </Link>
                </div>
            </div>
        </div>
    );
}
